import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { RxCross2 } from 'react-icons/rx';

type DocumentMockupProps = {
  type: 'passport' | 'identity';
  className?: string;
};

export const DocumentMockup: React.FC<DocumentMockupProps> = ({ type, className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const isIdentity = type === 'identity';
  const title = isIdentity
    ? t('labels.lblIdentityExampleTitle') || t('labels.lblDocumentExample') || 'National ID example'
    : t('labels.lblPassportExampleTitle') || t('labels.lblDocumentExample') || 'Passport example';

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={`shrink-0 text-xs font-medium text-blue-600 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${className}`}
        >
          {t('labels.lblSeeExample') || 'See example'}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black bg-opacity-20 data-[state=open]:animate-overlayShow" />
        <Dialog.Content
          className={`fixed left-[50%] top-[50%] z-50 max-h-[85vh] w-[90vw] translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-[6px] bg-white p-4 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow ${
            isIdentity ? 'max-w-[880px]' : 'max-w-[560px]'
          }`}
        >
          <Dialog.Title className="m-0 pr-8 text-[17px] font-medium text-black">{title}</Dialog.Title>
          <Dialog.Description className={isIdentity ? 'mt-1 text-sm text-slate-500' : 'sr-only'}>
            {isIdentity
              ? t('labels.lblIdentityOpenHint') || 'Send the document open, with front and back visible'
              : t('labels.lblDocumentExample') || 'Document example'}
          </Dialog.Description>
          {isIdentity ? (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <figure className="min-w-0">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  <img
                    src="/img/finish-register/identity-front-mockup.png"
                    alt={t('labels.lblIdentityFrontAlt') || 'National ID front'}
                    className="h-auto w-full object-contain"
                  />
                </div>
                <figcaption className="mt-1.5 text-center text-xs text-slate-500">
                  {t('labels.lblIdentityFront') || 'Front'}
                </figcaption>
              </figure>
              <figure className="min-w-0">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  <img
                    src="/img/finish-register/identity-back-mockup.png"
                    alt={t('labels.lblIdentityBackAlt') || 'National ID back'}
                    className="h-auto w-full object-contain"
                  />
                </div>
                <figcaption className="mt-1.5 text-center text-xs text-slate-500">
                  {t('labels.lblIdentityBack') || 'Back'}
                </figcaption>
              </figure>
            </div>
          ) : (
            <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              <img
                src="/img/finish-register/passport-mockup.png"
                alt={t('labels.lblPassportMockupAlt') || 'Passport document example'}
                className="h-auto w-full object-contain"
              />
            </div>
          )}
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 focus:outline-none focus:shadow-[0_0_0_2px] focus:shadow-blue-400"
              aria-label="Close"
            >
              <RxCross2 />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
